import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import { DeleteForever } from '@mui/icons-material';

/**
 * Reusable Delete Confirmation Dialog with Delete Reason Input
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onClose - Function to call when dialog closes
 * @param {function} props.onConfirm - Function to call when delete is confirmed
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Confirmation message
 * @param {string} props.itemName - Name of item being deleted (for context)
 * @param {boolean} props.loading - Whether delete operation is in progress
 * @param {string} props.error - Error message to display
 * @param {boolean} props.requireReason - Whether delete reason is required
 * @param {string} props.reasonLabel - Label for reason input
 * @param {string} props.reasonPlaceholder - Placeholder for reason input
 * @param {number} props.reasonMaxLength - Maximum length for reason input
 */
const DeleteConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    title = 'Confirm Delete',
    message = 'Are you sure you want to delete this item? This action cannot be undone.',
    itemName = '',
    loading = false,
    error = null,
    requireReason = true,
    reasonLabel = 'Reason for deletion',
    reasonPlaceholder = 'Please provide a reason for deleting this item...',
    reasonMaxLength = 500,
}) => {
    const [deleteReason, setDeleteReason] = useState('');
    const [reasonError, setReasonError] = useState('');

    const handleConfirm = () => {
        if (requireReason && !deleteReason.trim()) {
            setReasonError('Please provide a reason for deletion');
            return;
        }

        if (deleteReason.length > reasonMaxLength) {
            setReasonError(`Reason must be less than ${reasonMaxLength} characters`);
            return;
        }

        setReasonError('');
        onConfirm(deleteReason.trim());
    };

    const handleClose = () => {
        setDeleteReason('');
        setReasonError('');
        onClose();
    };

    const handleReasonChange = event => {
        const value = event.target.value;
        setDeleteReason(value);

        if (reasonError && value.trim()) {
            setReasonError('');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth='sm'
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'error.main',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <DeleteForever />
                {title}
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant='body1' color='text.primary'>
                        {message}
                    </Typography>
                    {itemName && (
                        <Typography variant='body2' color='text.secondary' sx={{ mt: 1, fontStyle: 'italic' }}>
                            Item: {itemName}
                        </Typography>
                    )}
                </Box>

                {requireReason && (
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={reasonLabel}
                        placeholder={reasonPlaceholder}
                        value={deleteReason}
                        onChange={handleReasonChange}
                        error={!!reasonError}
                        helperText={reasonError || `${deleteReason.length}/${reasonMaxLength} characters`}
                        disabled={loading}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                    borderColor: 'error.main',
                                },
                            },
                        }}
                    />
                )}

                {error && (
                    <Alert severity='error' sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={handleClose} disabled={loading} variant='outlined'>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    color='error'
                    variant='contained'
                    disabled={loading || (requireReason && !deleteReason.trim())}
                    startIcon={loading ? <CircularProgress size={16} /> : <DeleteForever />}
                    sx={{
                        minWidth: 120,
                        '&:hover': {
                            backgroundColor: 'error.dark',
                        },
                    }}
                >
                    {loading ? 'Deleting...' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmationDialog;
